"use client";

import { BlockNoteView } from "@blocknote/mantine";
import { DefaultReactGridSuggestionItem, DefaultReactSuggestionItem, GridSuggestionMenuController, GridSuggestionMenuProps, SuggestionMenuController, useCreateBlockNote } from "@blocknote/react";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { useEffect, useState } from "react";
import * as Y from "yjs";

import "@blocknote/mantine/style.css";
import "./common.css";

import { BlockNoteSchema, defaultInlineContentSpecs, filterSuggestionItems, PartialBlock } from "@blocknote/core";
import Swal from "sweetalert2";

import {
  fetchWikiData,
  frontEndPoint,
  getArtifactDetails,
  getInitiativeDetails,
  getRandomColorFromSet,
  handleRedirections,
  saveToStorage,
  setSubscriptionAndInitiativeId,
  uploadFile
} from "./common";

import { Mention } from "./Mention";

// 🔐 Extract encoded data
const search = window.location.search;
const urlParams = new URLSearchParams(search);
const encodedData = urlParams.get("data");

let jsonData = {
  alias: "",
  initiativeId: "",
  id: 0,
  userName: "",
  subscriptionId: "",
  teamId: 0,
  isAssociatedWithWorkItem: false,
  environmentName: "dev",
  userId: 0,
};

if (encodedData) {
  const decodedData = atob(encodedData);
  const {
    alias,
    initiativeId,
    id,
    subscriptionId,
    userName,
    teamId = 0,
    isAssociatedWithWorkItem = false,
    environmentName,
    userId = 0,
  } = JSON.parse(decodedData);

  if (!alias || !initiativeId || !id || !subscriptionId || !userName) {
    handleRedirections();
  }

  jsonData = {
    alias,
    initiativeId,
    id,
    subscriptionId,
    userName,
    teamId,
    isAssociatedWithWorkItem,
    environmentName,
    userId,
  };
} else {
  handleRedirections();
}

if (!jsonData.subscriptionId || !jsonData.initiativeId) {
  //@ts-ignore
  Swal.fire({
    icon: "warning",
    title: "Not Logged In",
    text: "You are not logged in. Please log in to continue.",
    showConfirmButton: false,
    timer: null,
    backdrop: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showClass: { popup: "" },
    hideClass: { popup: "" },
  });

  throw new Error("You are not logged in. Please log in to continue.");
}

// 🧠 Set default state
setSubscriptionAndInitiativeId(jsonData);

// 📜 Define schema with mention
const schema = BlockNoteSchema.create({
  inlineContentSpecs: {
    ...defaultInlineContentSpecs,
    mention: Mention,
  },
});

function CustomEmojiPicker(
  props: GridSuggestionMenuProps<DefaultReactGridSuggestionItem>,
) {
  return (
    <div
      className={"emoji-picker"}
      style={
        { gridTemplateColumns: `repeat(${props.columns || 1}, 1fr)` } as any
      }
    >
      {props.items.map((item, index) => (
        <div
          className={`emoji-picker-item ${props.selectedIndex === index ? "selected" : ""
            }`}
          onClick={() => {
            props.onItemClick?.(item);
          }}
        >
          {item.icon}
        </div>
      ))}
    </div>
  );
}

// 🌟 Top-level component with YDocProvider
export default function App() {
  return (
    <Document />
  );
}

// 📄 Main editor document component
let initiativeDetails: any = [];
let artifactDetails: any = [];
const docId = btoa(`${jsonData.subscriptionId}_${jsonData.initiativeId}_${jsonData.id}`);

function Document() {
  const doc = new Y.Doc();
  const provider: any = new HocuspocusProvider({
    url: "wss://live.shorterloop.com/",
    name: docId,
    document: doc,
    parameters: { userId: jsonData?.userId },
    onClose: ({ event }) => {
      if (event?.reason === 'Forbidden') {
        //@ts-ignore
        return Swal.fire({
          icon: "",
          title: "Error",
          text: "It looks like you're already editing this document in another tab. Please continue your work there to avoid conflicts.",
          showConfirmButton: false,
          timer: null,
          backdrop: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
          showClass: { popup: '' }, // Disables animation
          hideClass: { popup: '' }  // Disables animation
        });
      }
    },
  });

  const [loading, setLoading] = useState(true);

  const [blocks, setBlocks] = useState<PartialBlock[]>();

  const fragment = doc.getXmlFragment("document-store");
  const editor = useCreateBlockNote({
    schema,
    initialContent: blocks,
    collaboration: {
      // The Yjs Provider responsible for transporting updates:
      provider,
      // Where to store BlockNote data in the Y.Doc:
      fragment,
      // Information (name and color) for this user:
      user: {
        name: jsonData?.userName,
        color: getRandomColorFromSet(),
      },
    },
    uploadFile,
    trailingBlock: false,
  });


  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (initialized || !provider.synced || !editor) return;

      const isEditorEmpty =
        editor.document.length === 1 &&
        editor.document[0]?.type === "paragraph" &&
        editor.document[0]?.content?.length === 0;
      const treeWalker = fragment.createTreeWalker(() => true);
      const hasData = !!treeWalker.next().value;

      // Only populate if there's no remote content synced
      if (isEditorEmpty && !hasData) {
        await fetchWikiData(jsonData, editor);
      }
      setInitialized(true);
      setLoading(false);

      initiativeDetails = await getInitiativeDetails();
      artifactDetails = await getArtifactDetails();
    };

    // Attempt initialization as soon as provider is synced
    if (provider.synced && !initialized) {
      init();
    }

    // Listen for sync event for delayed initialization
    const handleSync = () => {
      if (!initialized) {
        init();
      }
    };

    provider.on("sync", handleSync);

    return () => {
      provider.off("sync", handleSync);
    };
  }, [provider, editor, initialized]);
  if (loading) return <div>Loading...</div>;

  const getMentionMenuItems = (): DefaultReactSuggestionItem[] => {
    const users = initiativeDetails.map((item: any) => item.user.firstName);
    return users.map((user: any) => ({
      title: user,
      onItemClick: () =>
        editor.insertInlineContent([
          {
            type: "mention",
            props: { user },
          },
          " ",
        ]),
    }));
  };

  const getArtifactMenuItems = (): DefaultReactSuggestionItem[] => {
    return artifactDetails.map((artifact: any) => ({
      title: `${artifact.externalKey} ${artifact.text}`,
      onItemClick: () => {
        editor.createLink(`${frontEndPoint}${artifact.path}`, artifact.text);
      },
    }));
  };

  return (
    <BlockNoteView
      editor={editor}
      theme="light"
      onChange={() => {
        //@ts-ignore
        saveToStorage(editor, editor.document, jsonData);
      }}
    >
      <SuggestionMenuController
        triggerCharacter={"@"}
        getItems={async (query) =>
          //@ts-ignore
          filterSuggestionItems(getMentionMenuItems(), query)
        }
      />
      <GridSuggestionMenuController
        triggerCharacter={":"}
        gridSuggestionMenuComponent={CustomEmojiPicker}
        columns={10}
        minQueryLength={0}
      />
      <SuggestionMenuController
        triggerCharacter={"#"}
        getItems={async (query) =>
          //@ts-ignore
          filterSuggestionItems(getArtifactMenuItems(), query)
        }
      />
    </BlockNoteView>
  );
}
